import { supabase, callEdgeFunction } from './supabase';
import type {
  EmailTemplate,
  EmailTemplateVersion,
  EmailTemplateVariable,
  EmailTemplateAuditLog,
  EmailTemplateListFilters,
  EmailTemplateListResponse,
  UpdateEmailTemplateRequest,
  CreateEmailTemplateRequest,
  SendTestEmailRequest,
  SendTestEmailResponse,
  UpdateVariablesRequest,
} from '@/types/emailTemplate';

// =============================================
// Email Templates - List & Read
// =============================================

export async function getEmailTemplates(
  filters: EmailTemplateListFilters = {}
): Promise<EmailTemplateListResponse> {
  const { search, status = 'all', trigger_type = 'all', page = 1, limit = 20 } = filters;

  let query = supabase
    .from('email_templates')
    .select('*, email_template_versions!inner(version_number)', { count: 'exact' })
    .eq('email_template_versions.is_current', true)
    .order('updated_at', { ascending: false })
    .range((page - 1) * limit, page * limit - 1);

  // Apply filters
  if (status === 'active') {
    query = query.eq('is_active', true);
  } else if (status === 'inactive') {
    query = query.eq('is_active', false);
  }

  if (trigger_type !== 'all') {
    query = query.eq('trigger_type', trigger_type);
  }

  if (search) {
    query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%,slug.ilike.%${search}%`);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  // Get version counts for each template
  const templates = (data || []).map(t => ({
    ...t,
    version_count: t.email_template_versions?.[0]?.version_number || 1,
  })) as EmailTemplate[];

  return {
    templates,
    total: count || 0,
    page,
    limit,
    hasMore: (count || 0) > page * limit,
  };
}

export async function getEmailTemplate(id: string): Promise<EmailTemplate | null> {
  const { data, error } = await supabase
    .from('email_templates')
    .select(`
      *,
      current_version:email_template_versions!inner(
        id, template_id, version_number, version_label,
        subject_template, html_content, text_content,
        change_summary, is_current, created_at, created_by
      ),
      variables:email_template_variables(
        id, template_id, variable_name, variable_key,
        description, data_type, is_required, default_value,
        example_value, source_table, source_field, sort_order
      )
    `)
    .eq('id', id)
    .eq('email_template_versions.is_current', true)
    .order('sort_order', { referencedTable: 'email_template_variables', ascending: true })
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data ? {
    ...data,
    current_version: data.current_version?.[0] || null,
  } as EmailTemplate : null;
}

export async function getEmailTemplateBySlug(slug: string): Promise<EmailTemplate | null> {
  const { data, error } = await supabase
    .from('email_templates')
    .select(`
      *,
      current_version:email_template_versions!inner(
        id, template_id, version_number, version_label,
        subject_template, html_content, text_content,
        change_summary, is_current, created_at
      ),
      variables:email_template_variables(
        id, variable_name, variable_key, description,
        data_type, is_required, example_value, sort_order
      )
    `)
    .eq('slug', slug)
    .eq('is_active', true)
    .eq('email_template_versions.is_current', true)
    .order('sort_order', { referencedTable: 'email_template_variables', ascending: true })
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data ? {
    ...data,
    current_version: data.current_version?.[0] || null,
  } as EmailTemplate : null;
}

// =============================================
// Email Templates - Create & Update
// =============================================

export async function updateEmailTemplate(
  id: string,
  updates: UpdateEmailTemplateRequest,
  adminUserId?: string
): Promise<EmailTemplate> {
  const { change_summary, ...templateUpdates } = updates;

  // Get current template to compare changes
  const { data: currentTemplate, error: fetchError } = await supabase
    .from('email_templates')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Update the template
  const { data: updatedTemplate, error: updateError } = await supabase
    .from('email_templates')
    .update({
      ...templateUpdates,
      updated_by: adminUserId,
    })
    .eq('id', id)
    .select()
    .single();

  if (updateError) throw updateError;

  // Create a new version if content changed
  const contentChanged =
    updates.subject_template !== undefined ||
    updates.html_content !== undefined ||
    updates.text_content !== undefined;

  if (contentChanged) {
    // Get next version number
    const { data: versionNum } = await supabase
      .rpc('get_next_template_version_number', { p_template_id: id });

    const versionLabel = `v${versionNum}_${formatDateForVersion(new Date())}`;

    // Create new version
    const { error: versionError } = await supabase
      .from('email_template_versions')
      .insert({
        template_id: id,
        version_number: versionNum,
        version_label: versionLabel,
        subject_template: updates.subject_template || currentTemplate.subject_template,
        html_content: updates.html_content || currentTemplate.html_content,
        text_content: updates.text_content || currentTemplate.text_content,
        change_summary: change_summary || 'Template updated',
        is_current: true,
        created_by: adminUserId,
      });

    if (versionError) throw versionError;

    // Log the audit
    await supabase.from('email_template_audit_log').insert({
      template_id: id,
      admin_user_id: adminUserId,
      action: 'updated',
      old_values: {
        subject_template: currentTemplate.subject_template,
        html_content: currentTemplate.html_content,
        text_content: currentTemplate.text_content,
      },
      new_values: {
        subject_template: updates.subject_template || currentTemplate.subject_template,
        html_content: updates.html_content || currentTemplate.html_content,
        text_content: updates.text_content || currentTemplate.text_content,
      },
      notes: change_summary,
    });
  }

  return updatedTemplate as EmailTemplate;
}

export async function createEmailTemplate(
  template: CreateEmailTemplateRequest,
  adminUserId?: string
): Promise<EmailTemplate> {
  // Create the template
  const { data: newTemplate, error: createError } = await supabase
    .from('email_templates')
    .insert({
      ...template,
      created_by: adminUserId,
      updated_by: adminUserId,
    })
    .select()
    .single();

  if (createError) throw createError;

  // Create initial version
  const versionLabel = `v1_${formatDateForVersion(new Date())}`;

  const { error: versionError } = await supabase
    .from('email_template_versions')
    .insert({
      template_id: newTemplate.id,
      version_number: 1,
      version_label: versionLabel,
      subject_template: template.subject_template,
      html_content: template.html_content,
      text_content: template.text_content || null,
      change_summary: 'Initial version',
      is_current: true,
      created_by: adminUserId,
    });

  if (versionError) throw versionError;

  // Log the creation
  await supabase.from('email_template_audit_log').insert({
    template_id: newTemplate.id,
    admin_user_id: adminUserId,
    action: 'created',
    new_values: template,
    notes: 'Template created',
  });

  return newTemplate as EmailTemplate;
}

export async function toggleTemplateActive(
  id: string,
  isActive: boolean,
  adminUserId?: string
): Promise<void> {
  const { error } = await supabase
    .from('email_templates')
    .update({
      is_active: isActive,
      updated_by: adminUserId,
    })
    .eq('id', id);

  if (error) throw error;

  // Log the action
  await supabase.from('email_template_audit_log').insert({
    template_id: id,
    admin_user_id: adminUserId,
    action: isActive ? 'activated' : 'deactivated',
    notes: isActive ? 'Template activated' : 'Template deactivated',
  });
}

// =============================================
// Version History
// =============================================

export async function getTemplateVersions(
  templateId: string,
  page = 1,
  limit = 20
): Promise<{ versions: EmailTemplateVersion[]; total: number }> {
  const { data, count, error } = await supabase
    .from('email_template_versions')
    .select('*', { count: 'exact' })
    .eq('template_id', templateId)
    .order('version_number', { ascending: false })
    .range((page - 1) * limit, page * limit - 1);

  if (error) throw error;

  return {
    versions: data || [],
    total: count || 0,
  };
}

export async function getTemplateVersion(versionId: string): Promise<EmailTemplateVersion | null> {
  const { data, error } = await supabase
    .from('email_template_versions')
    .select('*')
    .eq('id', versionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as EmailTemplateVersion;
}

export async function revertToVersion(
  templateId: string,
  versionId: string,
  adminUserId?: string
): Promise<EmailTemplate> {
  // Get the version to revert to
  const { data: targetVersion, error: versionError } = await supabase
    .from('email_template_versions')
    .select('*')
    .eq('id', versionId)
    .eq('template_id', templateId)
    .single();

  if (versionError) throw versionError;
  if (!targetVersion) throw new Error('Version not found');

  // Update the template with the version's content
  const { data: updatedTemplate, error: updateError } = await supabase
    .from('email_templates')
    .update({
      subject_template: targetVersion.subject_template,
      html_content: targetVersion.html_content,
      text_content: targetVersion.text_content,
      updated_by: adminUserId,
    })
    .eq('id', templateId)
    .select()
    .single();

  if (updateError) throw updateError;

  // Get next version number
  const { data: versionNum } = await supabase
    .rpc('get_next_template_version_number', { p_template_id: templateId });

  const versionLabel = `v${versionNum}_${formatDateForVersion(new Date())}`;

  // Create a new version (revert creates a new version, doesn't delete history)
  const { error: newVersionError } = await supabase
    .from('email_template_versions')
    .insert({
      template_id: templateId,
      version_number: versionNum,
      version_label: versionLabel,
      subject_template: targetVersion.subject_template,
      html_content: targetVersion.html_content,
      text_content: targetVersion.text_content,
      change_summary: `Reverted to ${targetVersion.version_label}`,
      is_current: true,
      created_by: adminUserId,
    });

  if (newVersionError) throw newVersionError;

  // Log the revert
  await supabase.from('email_template_audit_log').insert({
    template_id: templateId,
    version_id: versionId,
    admin_user_id: adminUserId,
    action: 'reverted',
    notes: `Reverted to version ${targetVersion.version_label}`,
  });

  return updatedTemplate as EmailTemplate;
}

// =============================================
// Variables Management
// =============================================

export async function getTemplateVariables(templateId: string): Promise<EmailTemplateVariable[]> {
  const { data, error } = await supabase
    .from('email_template_variables')
    .select('*')
    .eq('template_id', templateId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function updateTemplateVariables(
  request: UpdateVariablesRequest
): Promise<EmailTemplateVariable[]> {
  const { template_id, variables } = request;

  // Delete existing variables
  const { error: deleteError } = await supabase
    .from('email_template_variables')
    .delete()
    .eq('template_id', template_id);

  if (deleteError) throw deleteError;

  // Insert new variables
  if (variables.length > 0) {
    const variablesToInsert = variables.map((v, index) => ({
      ...v,
      template_id,
      sort_order: v.sort_order ?? index,
    }));

    const { data, error: insertError } = await supabase
      .from('email_template_variables')
      .insert(variablesToInsert)
      .select();

    if (insertError) throw insertError;
    return data || [];
  }

  return [];
}

// =============================================
// Audit Log
// =============================================

export async function getTemplateAuditLog(
  templateId: string,
  page = 1,
  limit = 50
): Promise<{ logs: EmailTemplateAuditLog[]; total: number }> {
  const { data, count, error } = await supabase
    .from('email_template_audit_log')
    .select('*', { count: 'exact' })
    .eq('template_id', templateId)
    .order('created_at', { ascending: false })
    .range((page - 1) * limit, page * limit - 1);

  if (error) throw error;

  return {
    logs: data || [],
    total: count || 0,
  };
}

// =============================================
// Test Email
// =============================================

export async function sendTestEmail(request: SendTestEmailRequest): Promise<SendTestEmailResponse> {
  return callEdgeFunction<SendTestEmailResponse>('send-test-email', request);
}

// =============================================
// Duplicate Template
// =============================================

export async function duplicateTemplate(
  templateId: string,
  newSlug: string,
  newName: string,
  adminUserId?: string
): Promise<EmailTemplate> {
  // Get the original template
  const original = await getEmailTemplate(templateId);
  if (!original) throw new Error('Template not found');

  // Create the duplicate
  return createEmailTemplate({
    slug: newSlug,
    name: newName,
    description: `Copy of ${original.name}`,
    subject_template: original.subject_template,
    html_content: original.html_content,
    text_content: original.text_content || undefined,
    from_name: original.from_name,
    from_email: original.from_email,
    reply_to_email: original.reply_to_email || undefined,
    default_to: original.default_to || undefined,
    cc_addresses: original.cc_addresses || undefined,
    bcc_addresses: original.bcc_addresses || undefined,
    trigger_type: original.trigger_type,
    trigger_description: original.trigger_description,
    trigger_function: `${original.trigger_function}-copy`,
    trigger_screen: original.trigger_screen || undefined,
    cadence: original.cadence || undefined,
  }, adminUserId);
}

// =============================================
// Helper Functions
// =============================================

function formatDateForVersion(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}${day}${year}`;
}

/**
 * Render a template by replacing {{variable}} placeholders with values
 */
export function renderTemplate(
  content: string,
  variables: Record<string, string>
): string {
  let rendered = content;

  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    rendered = rendered.replace(regex, value);
  }

  return rendered;
}

/**
 * Extract variable keys from template content
 */
export function extractVariableKeys(content: string): string[] {
  const regex = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const keys: string[] = [];
  let match;

  while ((match = regex.exec(content)) !== null) {
    if (!keys.includes(match[1])) {
      keys.push(match[1]);
    }
  }

  return keys;
}

/**
 * Build example variables map from template variables
 */
export function buildExampleVariables(
  variables: EmailTemplateVariable[]
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const v of variables) {
    result[v.variable_key] = v.example_value || v.default_value || `{{${v.variable_key}}}`;
  }

  return result;
}
